import { FaireProtectionTooltip } from "@faire/deprecated-retailer-visitor-shared/components/BrandCartCard/components/FaireProtectionTooltip";
import {
  isDiscontinued as isProductOptionDiscontinued,
  isProductOptionInStock,
} from "@faire/deprecated-retailer-visitor-shared/lib/productOptions";
import { useCanAddToCart } from "@faire/deprecated-retailer-visitor-shared/lib/useCanAddToCart";
import { ProductAddToCartState } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/AddToCart/useProductAddToCartState";
import { useMutation as useExpectedAddToCartAmountComputeMutation } from "@faire/web-api--source/endpoints/www/api/v2/carts/expected-add-to-cart-amount/compute/post-hooks";
import { IComputeExpectedAddToCartAmountRequest } from "@faire/web-api--source/faire/carts/IComputeExpectedAddToCartAmountRequest";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { usePrevious } from "@faire/web/ui/hooks/usePrevious";
import isEqual from "lodash/isEqual";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { isAddToCartDisabled } from "./isAddToCartDisabled";
import { isSelectedProductDifferentThanCart } from "./isSelectedProductDifferentThanCart";
import { useAddToCartText } from "./useAddToCartText";

const ADD_TO_CART_CTA_STATE = [
  "UNAVAILABLE",
  "UPDATE_DEFAULT",
  "UPDATE_DIFFERENT_PRODUCT",
  "PREORDER_DEFAULT",
  "PREORDER_IN_PROGRESS",
  "DEFAULT",
  "DEFAULT_IN_PROGRESS",
] as const;

export type AddToCartCtaState = (typeof ADD_TO_CART_CTA_STATE)[number];

interface IProps {
  brandToken: string | undefined;
  addToCartNotificationInProgress: boolean;
  isBrandActive: boolean | undefined;
  isInvoice: boolean | undefined;
  isCartInfoLoading: boolean | undefined;
  isFaireProtectionLoading: boolean;
  productAddToCartState: ProductAddToCartState;
  product: IProductPageResponse.IPdpProduct;
  productAddCTAOverride?: (
    isUpdating: boolean,
    strictLocalize: StrictLocalizeFunction
  ) => string;
  updateCart: () => Promise<void>;
  isBrandTokenForbidden: (
    brandToken: string,
    options?:
      | {
          trackReason?: true | undefined;
        }
      | undefined
  ) => boolean;
}

export const AddToCartButton: React.FC<IProps> = ({
  brandToken,
  isInvoice,
  isBrandActive,
  addToCartNotificationInProgress,
  isCartInfoLoading,
  isFaireProtectionLoading,
  productAddToCartState,
  product,
  productAddCTAOverride,
  updateCart,
  isBrandTokenForbidden,
}) => {
  const canAdd = useCanAddToCart();
  const addToCartCtaState = getAddToCartCtaState(
    productAddToCartState,
    addToCartNotificationInProgress,
    !!isBrandActive,
    product
  );
  const {
    selectedOption,
    updateQuantityOnChange,
    productOptionInCart,
    perStyleOptionVariations,
  } = productAddToCartState;
  const { getExpectedAddToCartItems } = productAddToCartState;
  const { mutateAsync: computeExpectedAddToCartAmountMutation } =
    useExpectedAddToCartAmountComputeMutation({});
  const [expectedAddToCartAmount, setExpectedAddToCartAmount] = useState<
    IFaireMoney | undefined
  >(undefined);
  const items = useMemo(
    () => getExpectedAddToCartItems(),
    [getExpectedAddToCartItems]
  );
  const prevItems = usePrevious(items);

  useEffect(() => {
    const computeExpectedAddToCartAmount = async () => {
      if (items?.length === 0 || isEqual(items, prevItems)) {
        return;
      }

      try {
        const response = await computeExpectedAddToCartAmountMutation([
          IComputeExpectedAddToCartAmountRequest.build({
            add_to_cart_items: items,
          }),
        ]);
        setExpectedAddToCartAmount(response.total_amount);
      } catch (error) {
        setExpectedAddToCartAmount(undefined);
      }
    };

    computeExpectedAddToCartAmount();
  }, [computeExpectedAddToCartAmountMutation, items, prevItems]);

  const addToCartText = useAddToCartText(
    addToCartCtaState,
    !!(updateQuantityOnChange && productOptionInCart),
    productAddCTAOverride,
    expectedAddToCartAmount
  );

  if (
    selectedOption &&
    isBrandActive &&
    !isProductOptionDiscontinued(product, selectedOption) &&
    !isProductOptionInStock(selectedOption, perStyleOptionVariations)
  ) {
    return null;
  }

  const brandTokenForbidden = isBrandTokenForbidden(brandToken ?? "");
  const disableButton = isAddToCartDisabled(
    product,
    productAddToCartState,
    isBrandActive,
    isInvoice,
    canAdd
  );

  if (brandTokenForbidden && !isCartInfoLoading) {
    return (
      <CtaContainer>
        <FaireProtectionTooltip
          hidden={false}
          targetNode={
            <AddToCart
              disabled
              onClick={updateCart}
              data-test-id="addToCartButton"
            >
              {addToCartText}
            </AddToCart>
          }
        />
      </CtaContainer>
    );
  }

  return (
    <AddToCart
      disabled={disableButton}
      loading={isCartInfoLoading || isFaireProtectionLoading}
      onClick={updateCart}
      data-test-id="addToCartButton"
    >
      {addToCartText}
    </AddToCart>
  );
};

const getAddToCartCtaState = (
  productAddToCartState: ProductAddToCartState,
  addToCartNotificationInProgress: boolean,
  isBrandActive: boolean,
  product: IProductPageResponse.IPdpProduct
): AddToCartCtaState => {
  const {
    selectedOption,
    updateQuantityOnChange,
    productOptionInCart,
    isPreorderableSelected,
  } = productAddToCartState;

  const isRemovedFromCart =
    updateQuantityOnChange &&
    !productOptionInCart &&
    addToCartNotificationInProgress;
  const updateProductInCart = updateQuantityOnChange && !!productOptionInCart;
  const isDifferentThanCart = isSelectedProductDifferentThanCart(
    productAddToCartState
  );
  const isDiscontinued = isProductOptionDiscontinued(product, selectedOption);

  if (selectedOption === undefined || !isBrandActive || isDiscontinued) {
    return "UNAVAILABLE";
  }
  if (updateProductInCart) {
    return isDifferentThanCart ? "UPDATE_DIFFERENT_PRODUCT" : "UPDATE_DEFAULT";
  }
  if (isPreorderableSelected) {
    return addToCartNotificationInProgress
      ? "PREORDER_IN_PROGRESS"
      : "PREORDER_DEFAULT";
  }
  return isRemovedFromCart || !addToCartNotificationInProgress
    ? "DEFAULT"
    : "DEFAULT_IN_PROGRESS";
};

const CtaContainer = styled(Column)`
  width: 100%;
`;

const AddToCart = styled(Button)`
  width: 100%;
`;
